import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { BookOpenIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { libraryAPI } from '../../utils/api';

// Friendly labels + display order for each doc_type. The order here drives how
// result groups are stacked on the page.
const DOC_TYPE_GROUPS = [
  { key: 'constitution', label: 'Constitution' },
  { key: 'act', label: 'Acts' },
  { key: 'si', label: 'Statutory Instruments' },
  { key: 'regulation', label: 'Regulations' },
  { key: 'rules', label: 'Rules of Court' },
  { key: 'case', label: 'Cases' },
  { key: 'other', label: 'Other' },
];

const DOC_TYPE_LABEL = DOC_TYPE_GROUPS.reduce((acc, g) => {
  acc[g.key] = g.label;
  return acc;
}, {});

const TYPE_BADGE = 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300';

const DocTypeBadge = ({ docType }) => (
  <span className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${TYPE_BADGE}`}>
    {DOC_TYPE_LABEL[docType] || 'Document'}
  </span>
);

const LibraryHome = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [activeType, setActiveType] = useState('');

  const fetchDocuments = useCallback(async (search, docType) => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (search) params.search = search;
      if (docType) params.doc_type = docType;
      const res = await libraryAPI.getDocuments(params);
      const data = res.data?.results || res.data || [];
      setDocuments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Could not load the library. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments(activeSearch, activeType);
  }, [fetchDocuments, activeSearch, activeType]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setActiveSearch(searchInput.trim());
  };

  // Group documents by doc_type, preserving the friendly group order.
  const grouped = DOC_TYPE_GROUPS
    .map((g) => ({ ...g, items: documents.filter((d) => d.doc_type === g.key) }))
    .filter((g) => g.items.length > 0);

  // Anything with an unrecognised doc_type still gets shown under "Other".
  const knownTypes = new Set(DOC_TYPE_GROUPS.map((g) => g.key));
  const unknown = documents.filter((d) => !knownTypes.has(d.doc_type));
  if (unknown.length > 0) {
    const otherGroup = grouped.find((g) => g.key === 'other');
    if (otherGroup) {
      otherGroup.items = [...otherGroup.items, ...unknown];
    } else {
      grouped.push({ key: 'other', label: 'Other', items: unknown });
    }
  }

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 max-w-4xl">
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <BookOpenIcon className="h-6 w-6 text-primary-500" />
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Legal Library</h1>
        </div>
        <p className="mt-1 text-sm sm:text-base text-gray-600 dark:text-gray-300">
          Browse and read hosted Zimbabwean primary legal sources — the Constitution, Acts, and more.
        </p>
      </div>

      {/* Search + filter */}
      <form onSubmit={handleSubmit} className="mb-5 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search the library…"
            className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white sm:text-sm"
          />
        </div>
        <button
          type="submit"
          className="inline-flex justify-center items-center px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700"
        >
          Search
        </button>
      </form>

      {/* Type filter chips */}
      <div className="mb-6 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setActiveType('')}
          className={`px-3 py-1 text-sm rounded-full border transition-colors ${
            activeType === ''
              ? 'bg-primary-600 text-white border-primary-600'
              : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          All
        </button>
        {DOC_TYPE_GROUPS.map((g) => (
          <button
            key={g.key}
            type="button"
            onClick={() => setActiveType(g.key)}
            className={`px-3 py-1 text-sm rounded-full border transition-colors ${
              activeType === g.key
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            {g.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300 p-4 rounded-md text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500" />
        </div>
      ) : grouped.length === 0 && !error ? (
        <div className="text-center py-16">
          <BookOpenIcon className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" />
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            {activeSearch || activeType ? 'No documents match your search.' : 'No documents yet.'}
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.map((group) => (
            <section key={group.key}>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{group.label}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {group.items.map((doc) => (
                  <Link
                    key={doc.id}
                    to={`/library/${doc.id}`}
                    className="bg-white dark:bg-gray-800 rounded-lg shadow dark:shadow-gray-700/10 p-4 hover:shadow-md transition-shadow flex flex-col"
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="text-base font-medium text-gray-900 dark:text-white break-words">
                        {doc.title}
                      </h3>
                      <DocTypeBadge docType={doc.doc_type} />
                    </div>
                    {doc.citation && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{doc.citation}</p>
                    )}
                    {doc.summary && (
                      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 line-clamp-3">{doc.summary}</p>
                    )}
                    <span className="mt-3 text-xs font-medium text-primary-600 dark:text-primary-400">Read →</span>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

export default LibraryHome;
